/*
 * @Author: Ryan
 * @Date: 2026-01-21
 * @Description: 写入目标页面 Store 的工具函数
 */

import { useProductAvatarStore } from '@/3.0/ai-product-avatar/store';
import { useTextToImageStore } from '@/3.0/image-generator/store/textToImageStore';
import { useImageToVideoStore } from '@/3.0/video-generation/store/imageToVideoStore';
import { useTextToVideoStore } from '@/3.0/video-generation/store/textToVideoStore';
import { ExtendedUploadFile, FILE_STATUS } from '@/components/FileUpload';

import { useUGCNavigationStore } from '../store';

/**
 * 将数据写入目标页面的 store
 * @param targetKey - 目标类型 key（对应 config.ts 中 generatorOptions 的 key）
 * @param prompt - 文本提示词
 * @param fileList - 上传的文件列表
 * @param hasUploadedImage - 是否有上传图片（决定写入文生还是图生 store）
 * @param extraFormValues - 额外表单值（如 modelID、aspect_ratio、duration、resolution 等）
 * @param lastFrameFileList - 尾帧文件列表（仅 I2V 部分模型支持）
 */
export const writeToTargetStore = (
  targetKey: string,
  prompt: string,
  fileList: ExtendedUploadFile[],
  hasUploadedImage: boolean,
  extraFormValues?: Record<string, any>,
  lastFrameFileList?: ExtendedUploadFile[],
) => {
  // 过滤出上传成功的文件
  const successFiles = fileList.filter((f) => f.status === FILE_STATUS.SUCCESS);

  // Video from Product Image -> ai-product-avatar 产品区域
  if (targetKey === 'video-from-product-image') {
    useProductAvatarStore.getState().setScript(prompt);
    if (successFiles[0]) {
      useProductAvatarStore.getState().setProductInfo({
        url: successFiles[0].url!,
        previewUrl: successFiles[0].url!,
        name: successFiles[0].name,
      });
    }
    return;
  }

  // Video from Avatar Image -> ai-product-avatar 数字人区域
  if (targetKey === 'video-from-avatar-image') {
    useProductAvatarStore.getState().setScript(prompt);
    if (successFiles[0]) {
      useProductAvatarStore.getState().setAvatarInfo({
        url: successFiles[0].url!,
        previewUrl: successFiles[0].url!,
        title: successFiles[0].name,
      });
    }
    return;
  }

  // UGC Video Ads -> ai-video-generator
  if (targetKey === 'ugc-video-ads') {
    if (hasUploadedImage) {
      const values: Record<string, any> = {
        prompt,
        image: successFiles,
      };
      // 合并尾帧文件
      if (lastFrameFileList?.length) {
        const successLastFrames = lastFrameFileList.filter(
          (f) => f.status === FILE_STATUS.SUCCESS,
        );
        if (successLastFrames.length) {
          values.last_image = successLastFrames;
        }
      }
      // 合并额外表单值（modelID、aspect_ratio、duration、resolution 等）
      if (extraFormValues) {
        Object.assign(values, extraFormValues);
      }
      useImageToVideoStore.getState().setFormValues(values);
    } else {
      const values: Record<string, any> = { prompt };
      if (extraFormValues) {
        Object.assign(values, extraFormValues);
      }
      useTextToVideoStore.getState().setFormValues(values);
    }
    return;
  }

  // Generate Image -> ai-image-generator
  if (targetKey === 'generate-image') {
    if (hasUploadedImage) {
      useUGCNavigationStore.getState().setNavigationData('image-to-image', {
        prompt,
        images: successFiles,
      });
    } else {
      useTextToImageStore.getState().setFormValues({ prompt });
    }
    return;
  }
};
